/**
 * The location page's tab axis, as data.
 *
 * No React in this file on purpose. Everything here is a pure function over
 * ids and counts, so the rules that decide what the office sees can be tested
 * without mounting a page — and, more importantly, so one specific rule stops
 * living inside a component.
 *
 * ── Why TESTABLE_TRADES is here and not an early return ─────────────────────
 *
 * `trade-panel.tsx`'s TestsSection opened with
 *
 *     if (tradeCode !== "backflow" && tradeCode !== "fire_ext") return null;
 *     const [expanded, setExpanded] = useState(false);   // ← after the return
 *     const q = useQuery({ … });                          // ← after the return
 *
 * That is a conditional-hooks violation. It was harmless only because
 * `tradeCode` was fixed for the life of each mounted instance: one trade, one
 * tab, one component. The moment trade becomes a FILTER, a single instance
 * sees `backflow → plumbing → backflow`, the hook count changes between
 * renders of the same component, and React throws "rendered fewer hooks than
 * expected".
 *
 * So the gate moves out of the component and becomes a list. The tab renders
 * unconditionally and asks this module what to say.
 */

export const LOCATION_TABS = [
    { id: "jobs", label: "Jobs" },
    { id: "estimates", label: "Estimates" },
    { id: "invoices", label: "Invoices" },
    { id: "devices", label: "Devices" },
    { id: "tests", label: "Tests & results" },
    { id: "documents", label: "Documents" },
    { id: "photos", label: "Photos" },
] as const;

export type LocationTabId = (typeof LOCATION_TABS)[number]["id"];

export function isLocationTabId(value: unknown): value is LocationTabId {
    return typeof value === "string" && LOCATION_TABS.some((t) => t.id === value);
}

/**
 * Trades that have an inspection table behind them today. A zero-state for any
 * other trade would be misleading — there is no scheduling pipeline to be
 * empty of.
 */
export const TESTABLE_TRADES = ["backflow", "fire_ext"] as const;

export function isTestableTrade(code: string | null | undefined): boolean {
    return !!code && (TESTABLE_TRADES as ReadonlyArray<string>).includes(code);
}

/**
 * Why the Tests tab has nothing to show, in words, or `null` when it does.
 *
 * A hidden tab and an empty tab both tell the office nothing. A sentence tells
 * them the difference between "no tests yet" and "this trade doesn't have
 * tests".
 */
export function testsUnavailableReason(tradeCodes: readonly string[]): string | null {
    if (tradeCodes.some(isTestableTrade)) return null;
    if (tradeCodes.length === 0) return "No service lines on this location yet.";
    return "Test records exist for backflow and fire extinguisher work. This location has neither.";
}

export interface LocationView {
    tab: LocationTabId;
    /** `trades.code`, or null for "All". */
    trade: string | null;
}

/**
 * Which tab to land on.
 *
 * `trade-panel.tsx` opened the Tests section automatically when the trade had
 * overdue devices (`defaultOpen={summary.overdue_count > 0}`) — a recorded
 * do-not-regress rule. With tabs by record type, the equivalent is the default
 * ACTIVE TAB.
 *
 * Two things it must not do, both tested:
 *   - it must not fight the user. An explicit `?tab=devices` wins even at
 *     twelve overdue devices;
 *   - it must not re-fire. The caller reads this once, on first resolve, so a
 *     background refetch can never yank someone off the tab they opened.
 */
export function defaultLocationTab(opts: { urlTab?: string | null; overdueCount?: number }): LocationTabId {
    if (isLocationTabId(opts.urlTab)) return opts.urlTab;
    if ((opts.overdueCount ?? 0) > 0) return "tests";
    return "jobs";
}

/** Parse `?tab=&trade=` into a view, falling back rather than rendering blank. */
export function parseLocationView(
    params: { get(key: string): string | null },
    opts: { overdueCount?: number } = {},
): LocationView {
    const trade = params.get("trade");
    return {
        tab: defaultLocationTab({ urlTab: params.get("tab"), overdueCount: opts.overdueCount }),
        trade: trade && trade.trim() ? trade.trim() : null,
    };
}

/**
 * The view as query-string pairs, for a link somebody can send.
 * "All trades" omits `trade` entirely rather than writing `trade=all`, so the
 * server sees no filter and applies none.
 */
export function locationViewToSearch(view: Partial<LocationView>): Record<string, string> {
    const out: Record<string, string> = {};
    if (view.tab) out.tab = view.tab;
    if (view.trade) out.trade = view.trade;
    return out;
}
